import { useEffect, useMemo, useRef, useState } from "react";
import { FiX, FiHeart, FiChevronLeft, FiChevronRight } from "react-icons/fi";
import { useTranslation } from "react-i18next";
import AlternativeProductsBlock from "../AlternativeProductsBlock";
import ProductTabs from "../product/ProductTabs";
import "../../styles/ProductSheet.css";

const ANIM_MS = 450;
const CLOSE_DRAG_THRESHOLD = 120;
const MAX_DRAG_Y = 420;

function normalizeImages(product) {
  const arr = Array.isArray(product?.images) ? product.images : [];
  const normalized = arr
    .map((x) => ({
      id: String(x?.id ?? ""),
      url: x?.image_url || x?.image_proxy_url || "",
      sequence: Number(x?.sequence ?? 0),
    }))
    .filter((x) => x.id && x.url);

  if (!normalized.length) {
    const url = product?.image_url || product?.image_proxy_url || "";
    if (url) return [{ id: "main", url, sequence: 0 }];
  }

  normalized.sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0));
  return normalized;
}

function normalizeLang(lang) {
  return String(lang || "").toLowerCase().startsWith("en") ? "en" : "uk";
}

function getLocalizedName(product, lang = "uk") {
  const safeLang = normalizeLang(lang);

  if (safeLang === "en") {
    return product?.name_en || product?.name || product?.name_uk || "";
  }

  return product?.name_uk || product?.name || product?.name_en || "";
}

function getLocalizedDescription(product, lang = "uk") {
  const safeLang = normalizeLang(lang);

  if (safeLang === "en") {
    return (
      product?.description_en ||
      product?.description_text ||
      product?.description_uk ||
      ""
    );
  }

  return (
    product?.description_uk ||
    product?.description_text ||
    product?.description_en ||
    ""
  );
}

export default function ProductSheet({
  open,
  onClose,
  product,
  topOffset = 84,
  onToggleFavorite,
  isFavorite = false,
  onAddToCart,
  onOpenProduct,
}) {
  const { t, i18n } = useTranslation("product");

  const sheetRef = useRef(null);
  const sliderRef = useRef(null);
  const contentRef = useRef(null);

  const dragStartYRef = useRef(0);
  const dragDeltaYRef = useRef(0);
  const draggingRef = useRef(false);

  const [activeTab, setActiveTab] = useState("info");
  const [mounted, setMounted] = useState(open);
  const [closing, setClosing] = useState(false);
  const [dragY, setDragY] = useState(0);

  const images = useMemo(() => normalizeImages(product), [product]);
  const [imgIndex, setImgIndex] = useState(0);

  const currentLang = normalizeLang(
    i18n.resolvedLanguage || i18n.language || "uk"
  );

  const localizedProduct = useMemo(() => {
    const name = getLocalizedName(product, currentLang);
    const description_text = getLocalizedDescription(product, currentLang);

    return {
      ...product,
      name,
      description_text,
    };
  }, [product, currentLang]);

  function formatPriceUAH(price) {
    const n = Number(price || 0);
    const locale = currentLang === "uk" ? "uk-UA" : "en-US";

    const formatted = new Intl.NumberFormat(locale, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(n);

    return `${t("currency_symbol")}${formatted}`;
  }

  useEffect(() => {
    setImgIndex(0);
    if (sliderRef.current) sliderRef.current.scrollLeft = 0;
  }, [localizedProduct?.id, open]);

  useEffect(() => {
    setActiveTab("info");
    contentRef.current?.scrollTo?.({ top: 0, behavior: "smooth" });
  }, [localizedProduct?.id]);

  useEffect(() => {
    if (open) {
      setMounted(true);
      setClosing(false);
      setDragY(0);
      draggingRef.current = false;
      dragDeltaYRef.current = 0;
      return;
    }

    if (mounted) {
      setClosing(true);
      const tmr = setTimeout(() => {
        setMounted(false);
        setClosing(false);
        setDragY(0);
      }, ANIM_MS);
      return () => clearTimeout(tmr);
    }
  }, [open, mounted]);

  useEffect(() => {
    function onKey(e) {
      if (e.key === "Escape") onClose?.();
      if (images.length > 1) {
        if (e.key === "ArrowLeft") prevImage();
        if (e.key === "ArrowRight") nextImage();
      }
    }

    if (mounted) {
      document.addEventListener("keydown", onKey);
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mounted, onClose, images.length, imgIndex]);

  function clamp(n, min, max) {
    return Math.max(min, Math.min(n, max));
  }

  function handleDragStart(clientY) {
    if (closing) return;
    draggingRef.current = true;
    dragStartYRef.current = clientY;
    dragDeltaYRef.current = 0;
  }

  function handleDragMove(clientY) {
    if (!draggingRef.current) return;

    const delta = clientY - dragStartYRef.current;
    const next = clamp(delta, 0, MAX_DRAG_Y);

    dragDeltaYRef.current = next;
    setDragY(next);
  }

  function handleDragEnd() {
    if (!draggingRef.current) return;

    draggingRef.current = false;
    const shouldClose = dragDeltaYRef.current > CLOSE_DRAG_THRESHOLD;

    if (shouldClose) {
      setDragY(0);
      onClose?.();
      return;
    }

    dragDeltaYRef.current = 0;
    setDragY(0);
  }

  function onPointerDown(e) {
    handleDragStart(e.clientY);
  }

  function onPointerMove(e) {
    handleDragMove(e.clientY);
  }

  function onPointerUp() {
    handleDragEnd();
  }

  function onPointerCancel() {
    handleDragEnd();
  }

  if (!mounted) return null;

  const overlayClass = `psOverlay ${open && !closing ? "isOpen" : "isClosing"}`;
  const price = formatPriceUAH(localizedProduct?.price);
  const cats = localizedProduct?.categories || [];

  function handleBuyClick(e) {
    e.stopPropagation();

    const vid =
      localizedProduct?.variant_id ||
      localizedProduct?.product_id ||
      localizedProduct?.id;

    if (!vid) return;

    onAddToCart?.({
      ...localizedProduct,
      id: vid,
    });
  }

  function goToIndex(next) {
    const i = Math.max(0, Math.min(images.length - 1, next));
    setImgIndex(i);

    const el = sliderRef.current;
    if (!el) return;

    const w = el.clientWidth || 1;
    el.scrollTo({ left: i * w, behavior: "smooth" });
  }

  function prevImage() {
    if (images.length <= 1) return;
    goToIndex((imgIndex - 1 + images.length) % images.length);
  }

  function nextImage() {
    if (images.length <= 1) return;
    goToIndex((imgIndex + 1) % images.length);
  }

  function onSliderScroll() {
    const el = sliderRef.current;
    if (!el) return;
    const w = el.clientWidth || 1;
    const i = Math.round(el.scrollLeft / w);
    if (i !== imgIndex) setImgIndex(i);
  }

  const sheetStyle = dragY
    ? {
        transform: `translateY(${dragY}px)`,
        transition: "none",
      }
    : undefined;

  return (
    <div
      className={overlayClass}
      style={{ paddingTop: topOffset }}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="psSheet"
        ref={sheetRef}
        onClick={(e) => e.stopPropagation()}
        style={sheetStyle}
      >
        <div
          className="psHandleWrap"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerCancel}
        >
          <div className="psHandle" />
        </div>

        <div
          className="psTopBar"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerCancel}
        >
          <div className="psTopLeft">
            <div className="psTitle">
              {localizedProduct?.name || t("fallback_product")}
            </div>

            {!!cats.length && (
              <div className="psCats">
                <div className="psBreadcrumb">
                  {cats.slice(0, 2).map((c, i, arr) => (
                    <span
                      key={`${c}-${i}`}
                      className={`psBreadcrumbItem ${
                        i === arr.length - 1 ? "isActive" : ""
                      }`}
                    >
                      {c}
                      {i !== arr.length - 1 && (
                        <span className="psBreadcrumbSep"> / </span>
                      )}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="psActions">
            <button
              type="button"
              className={`psIconBtn ${isFavorite ? "isActive" : ""}`}
              onClick={() => onToggleFavorite?.(localizedProduct)}
              aria-label={t("favorite")}
              title={t("favorite")}
            >
              <FiHeart className="psIcon" />
            </button>

            <button
              type="button"
              className="psIconBtn"
              onClick={onClose}
              aria-label={t("close")}
              title={t("close")}
            >
              <FiX className="psIcon" />
            </button>
          </div>
        </div>

        <div
          className="psContent"
          ref={contentRef}
          style={{ paddingBottom: `calc(140px + env(safe-area-inset-bottom, 0px))` }}
        >
          <div className="psBody">
            {images.length > 0 && (
              <div className="psMedia">
                {images.length === 1 ? (
                  <div className="psProductImageWrap">
                    <img
                      className="psProductImage"
                      src={images[0].url}
                      alt={localizedProduct?.name || t("fallback_product")}
                    />
                  </div>
                ) : (
                  <div className="psCarousel" aria-label={t("gallery")}>
                    <div
                      className="psSlider"
                      ref={sliderRef}
                      onScroll={onSliderScroll}
                      role="group"
                      aria-roledescription="carousel"
                      aria-label={t("product_photos")}
                    >
                      {images.map((it) => (
                        <div className="psSlide" key={it.id}>
                          <img
                            className="psProductImage"
                            src={it.url}
                            alt={localizedProduct?.name || t("fallback_product")}
                          />
                        </div>
                      ))}
                    </div>

                    <button
                      type="button"
                      className="psNav psNavLeft"
                      onClick={(e) => {
                        e.stopPropagation();
                        prevImage();
                      }}
                      aria-label={t("prev_photo")}
                      title={t("prev_photo")}
                    >
                      <FiChevronLeft />
                    </button>

                    <button
                      type="button"
                      className="psNav psNavRight"
                      onClick={(e) => {
                        e.stopPropagation();
                        nextImage();
                      }}
                      aria-label={t("next_photo")}
                      title={t("next_photo")}
                    >
                      <FiChevronRight />
                    </button>

                    <div className="psDots" onClick={(e) => e.stopPropagation()}>
                      {images.map((it, i) => (
                        <button
                          key={it.id}
                          type="button"
                          className={`psDot ${i === imgIndex ? "isActive" : ""}`}
                          onClick={() => goToIndex(i)}
                          aria-label={t("photo_n", { count: i + 1 })}
                          title={t("photo_n", { count: i + 1 })}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            <ProductTabs
              activeTab={activeTab}
              onChangeTab={setActiveTab}
              product={localizedProduct}
            />

            <AlternativeProductsBlock
              excludeProductId={
                localizedProduct?.variant_id || localizedProduct?.id
              }
              onOpen={(p) => onOpenProduct?.(p)}
              onAdd={(p) => onAddToCart?.(p)}
            />
          </div>
        </div>

        <div className="psBuyBar" onClick={(e) => e.stopPropagation()}>
          <button
            type="button"
            className="psAddBtn"
            onClick={handleBuyClick}
            disabled={
              !(
                localizedProduct?.variant_id ||
                localizedProduct?.product_id ||
                localizedProduct?.id
              )
            }
          >
            <span className="psAddBtnLabel">{t("buy")}</span>
            <span className="psAddBtnPrice">{price}</span>
          </button>
        </div>
      </div>
    </div>
  );
}